import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaEnvelope, FaMobileAlt, FaLock, FaEye, FaEyeSlash } from "react-icons/fa";
import { FcGoogle } from "react-icons/fc";
import { motion } from "framer-motion";
import { toast } from "react-hot-toast";
import { signInWithPopup } from "firebase/auth";
import { auth, googleProvider } from "../firebase";
import { AuthContext } from "../context/AuthContext";
import api from "../utils/base_url";
import "../styles/Login.css";

/* ── All logic unchanged — only JSX structure/classNames updated ── */

const Login = () => {
  const [loginMethod, setLoginMethod] = useState("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState("");

  const { login, user } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      const redirectData = sessionStorage.getItem("login_redirect");
      if (redirectData) {
        const parsed = JSON.parse(redirectData);
        sessionStorage.removeItem("login_redirect");
        navigate(parsed.pathname, { replace: true, state: parsed.state });
      } else {
        navigate("/", { replace: true });
      }
    }
  }, [user, navigate]);

  useEffect(() => {
    const msg = sessionStorage.getItem("redirect_toast");
    if (msg) {
      toast.success(msg, { duration: 3000 });
      sessionStorage.removeItem("redirect_toast");
    }
  }, []);

  const isValidPhone = (phone) => /^[0-9]{10,15}$/.test(phone);

  const clearError = () => setTimeout(() => setErrors(""), 3000);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const res = await api.post("customer/login/", {
        email: email.trim().toLowerCase(),
        password,
      });
      login(res.data);
      toast.success("Logged in successfully!");
    } catch (error) {
      setErrors(error.response?.data?.error || "Invalid email or password");
      clearError();
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneLogin = async (e) => {
    e.preventDefault();
    if (!isValidPhone(phone)) return toast.error("Please enter a valid phone number.");
    setLoading(true);
    try {
      const res = await api.post("phone-login-step1/", { phone });
      sessionStorage.setItem("session_id", res.data.session_id);
      sessionStorage.setItem("phone", phone);
      toast.success(res.data.message || "OTP sent successfully!");
      navigate("/verification", { state: { type: "phone" } });
    } catch (error) {
      const msg = error.response?.data?.error || error.response?.data?.message || "Login failed";
      setErrors(msg);
      clearError();
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    setErrors("");
    try {
      const result = await signInWithPopup(auth, googleProvider);
      if (!result?.user) throw new Error("Google authentication cancelled");
      const idToken = await result.user.getIdToken(true);
      const res = await api.post("google/", { token: idToken });
      login(res.data);
      toast.success("Logged in with Google!");
    } catch (error) {
      let message = "Google login failed";
      let errorCode = error?.code || "UNKNOWN_GOOGLE_ERROR";
      if (error?.code) {
        switch (error.code) {
          case "auth/popup-closed-by-user": message = "Google login popup closed"; break;
          case "auth/popup-blocked": message = "Popup blocked. Please allow popups."; break;
          case "auth/network-request-failed": message = "Network error. Check your internet."; break;
          default: message = error.message || message;
        }
      }
      if (error?.response?.data?.error) { message = error.response.data.error; errorCode = "BACKEND_GOOGLE_ERROR"; }
      try {
        await api.post("frontend-auth-log/", {
          action: "GOOGLE_LOGIN", login_type: "GOOGLE", status: "FAILURE",
          message, error_code: errorCode, identifier: error?.customData?.email || null,
        });
      } catch (logError) { console.warn("Frontend auth log failed", logError); }
      setErrors(message);
      toast.error(message);
      clearError();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-wrapper">
      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
        className="login-card"
      >
        {/* Logo */}
        <img
          src="/mainlogo.png"
          alt="Nursery Logo"
          className="auth-logo"
        />

        {/* Title */}
        <h1 className="login-title">Welcome back</h1>
        <p className="login-subtitle">Sign in to your account</p>

        {/* Method toggle */}
        <div className="login-method-toggle">
          <button
            type="button"
            className={loginMethod === "email" ? "active" : ""}
            onClick={() => setLoginMethod("email")}
            aria-label="Login with email"
            title="Email login"
          >
            <FaEnvelope />
          </button>
          {/* Phone toggle — uncomment to enable */}
          {/* <button
            type="button"
            className={loginMethod === "phone" ? "active" : ""}
            onClick={() => setLoginMethod("phone")}
            aria-label="Login with phone"
            title="Phone login"
          >
            <FaMobileAlt />
          </button> */}
        </div>

        {/* ── Email form ── */}
        {loginMethod === "email" && (
          <motion.form
            onSubmit={handleEmailLogin}
            className="login-form"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
          >
            {/* Email field */}
            <div className="floating-input">
              <FaEnvelope className="field-icon" aria-hidden="true" />
              <input
                type="email"
                id="login-email"
                placeholder=" "
                value={email}
                required
                autoComplete="email"
                onChange={(e) => setEmail(e.target.value)}
              />
              <label htmlFor="login-email">Email address</label>
            </div>

            {/* Password field */}
            <div className="floating-input password-input">
              <FaLock className="field-icon" aria-hidden="true" />
              <input
                type={showPassword ? "text" : "password"}
                id="login-password"
                placeholder=" "
                value={password}
                required
                autoComplete="current-password"
                onChange={(e) => setPassword(e.target.value)}
              />
              <label htmlFor="login-password">Password</label>
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>

            {/* Error */}
            {errors && <p className="auth-error">{errors}</p>}

            {/* Forgot */}
            <div className="forgot-password">
              <Link to="/passwordreset">Forgot password?</Link>
            </div>

            {/* Submit */}
            <button
              type="submit"
              className="btn btn-primary login-btn"
              disabled={loading}
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </motion.form>
        )}

        {/* ── Phone form ── */}
        {loginMethod === "phone" && (
          <motion.form
            onSubmit={handlePhoneLogin}
            className="login-form"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
          >
            <div className="floating-input">
              <FaMobileAlt className="field-icon" aria-hidden="true" />
              <input
                type="tel"
                id="login-phone"
                placeholder=" "
                value={phone}
                required
                autoComplete="tel"
                onChange={(e) => setPhone(e.target.value.replace(/\D/, ""))}
                minLength={10}
                maxLength={10}
              />
              <label htmlFor="login-phone">Phone number</label>
            </div>

            {errors && <p className="auth-error">{errors}</p>}

            <button
              type="submit"
              className="btn btn-primary login-btn"
              disabled={loading}
            >
              {loading ? "Sending OTP…" : "Send OTP"}
            </button>
          </motion.form>
        )}

        {/* Divider */}
        <div className="auth-divider">
          <span>or continue with</span>
        </div>

        {/* Google */}
        <button
          type="button"
          onClick={handleGoogleLogin}
          className="btn-google-login"
          disabled={loading}
          aria-label="Sign in with Google"
        >
          <FcGoogle size={22} aria-hidden="true" />
          {loading ? "Please wait…" : "Continue with Google"}
        </button>

        {/* Register link */}
        <p className="register-prompt">
          New here?{" "}
          <Link to="/register" className="register-link">
            Create account
          </Link>
        </p>
      </motion.div>
    </div>
  );
};

export default Login;